import type { AtendenteDTO } from '@viniko/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  FileText,
  GraduationCap,
  Loader2,
  Play,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { Fragment, useState, type ReactNode } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { AvatarContato, Modal, Vazio, useToast } from '../../componentes/ui';
import { api } from '../../lib/api';
import { useAtendentes } from '../../lib/consultas';
import { Chat } from '../conversas/Chat';
import '../conversas/conversas.css';
import './treino.css';

interface Caso {
  conversaId: string;
  rodada: number;
  persona: { nome: string; empresa: string; material: string; caso: string; personalidade: string };
  encerrado: boolean;
  status: 'bot' | 'fila' | 'atendimento' | 'finalizada';
  atendenteId: string | null;
  ultimaMensagemEm: string | null;
  naoLidas: number;
  mensagens: number;
}

interface Analise {
  id: string;
  texto: string;
  conversas: number;
  criadoEm: string;
}

interface DadosTreino {
  iaDisponivel: boolean;
  casos: Caso[];
  analises: Analise[];
}

const QUANTIDADES = [5, 10, 15, 20];

const dataHora = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

function negrito(texto: string): ReactNode[] {
  return texto
    .split(/(\*\*[^*]+\*\*)/g)
    .map((p, i) =>
      p.startsWith('**') && p.endsWith('**') ? (
        <b key={i}>{p.slice(2, -2)}</b>
      ) : (
        <Fragment key={i}>{p}</Fragment>
      ),
    );
}

function Relatorio({ texto }: { texto: string }) {
  return (
    <div className="relatorio">
      {texto.split('\n').map((linha, i) => {
        const l = linha.trimEnd();
        if (/^#{1,2}\s/.test(l)) return <h2 key={i}>{negrito(l.replace(/^#+\s/, ''))}</h2>;
        if (/^#{3,}\s/.test(l)) return <h3 key={i}>{negrito(l.replace(/^#+\s/, ''))}</h3>;
        if (/^\s*[-*]\s/.test(l))
          return (
            <p key={i} className={`item-rel ${/^\s{2,}/.test(l) ? 'sub' : ''}`}>
              {negrito(l.replace(/^\s*[-*]\s/, ''))}
            </p>
          );
        if (!l.trim()) return null;
        return <p key={i}>{negrito(l)}</p>;
      })}
    </div>
  );
}

export function Treino({ eu }: { eu: AtendenteDTO }) {
  const { id, analiseId } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();
  const admin = eu.papel === 'admin';
  const atendentes = useAtendentes().data ?? [];
  const dados = useQuery({
    queryKey: ['treino'],
    queryFn: () => api<DadosTreino>('/api/treino'),
    refetchInterval: 8_000,
  });
  const [quantidade, setQuantidade] = useState(10);
  const [ocupado, setOcupado] = useState<'rodada' | 'analise' | null>(null);
  const [apagando, setApagando] = useState(false);

  const casos = dados.data?.casos ?? [];
  const analises = dados.data?.analises ?? [];
  const analise = analises.find((a) => a.id === analiseId);
  const respondidos = casos.filter((c) => c.mensagens > 1).length;

  async function novaRodada() {
    setOcupado('rodada');
    try {
      const r = await api<{ criados: number }>('/api/treino/rodada', {
        metodo: 'POST',
        corpo: { quantidade },
      });
      toast(`${r.criados} clientes de treino criados`);
      qc.invalidateQueries({ queryKey: ['treino'] });
    } catch (e) {
      toast((e as Error).message, 'erro');
    } finally {
      setOcupado(null);
    }
  }

  async function analisar() {
    setOcupado('analise');
    try {
      const a = await api<Analise>('/api/treino/analisar', { metodo: 'POST' });
      await qc.invalidateQueries({ queryKey: ['treino'] });
      navegar(`/treino/analise/${a.id}`);
    } catch (e) {
      toast((e as Error).message, 'erro');
    } finally {
      setOcupado(null);
    }
  }

  async function apagar() {
    try {
      await api('/api/treino', { metodo: 'DELETE' });
      qc.invalidateQueries({ queryKey: ['treino'] });
      toast('Treino apagado');
      setApagando(false);
      navegar('/treino');
    } catch (e) {
      toast((e as Error).message, 'erro');
    }
  }

  function situacao(c: Caso) {
    if (c.encerrado) return <span className="tr-status ok">encerrado</span>;
    if (c.status === 'atendimento') {
      const quem = atendentes.find((a) => a.id === c.atendenteId)?.nomeExibicao;
      return <span className="tr-status andamento">com {quem ?? 'alguém'}</span>;
    }
    return <span className="tr-status espera">esperando</span>;
  }

  return (
    <div className={`conversas treino ${id || analise ? 'com-chat' : ''}`}>
      <section className="lista" aria-label="Treino">
        <header className="lista-topo">
          <div className="lista-titulo">
            <h1>Treino</h1>
          </div>
          <p className="tr-ajuda">
            Atenda como se fosse um cliente de verdade: clique em <b>Assumir</b> e responda. Nada
            vai pro WhatsApp.
          </p>
          {admin && (
            <div className="tr-acoes">
              <select
                className="input"
                value={quantidade}
                aria-label="Quantos clientes"
                onChange={(e) => setQuantidade(Number(e.target.value))}
              >
                {QUANTIDADES.map((q) => (
                  <option key={q} value={q}>
                    {q} clientes
                  </option>
                ))}
              </select>
              <button
                className="btn primario"
                disabled={!!ocupado}
                onClick={() => void novaRodada()}
              >
                {ocupado === 'rodada' ? <Loader2 className="girando" /> : <Play />}
                {ocupado === 'rodada' ? 'Criando…' : 'Nova rodada'}
              </button>
              <button
                className="btn"
                disabled={!!ocupado || respondidos === 0}
                title={respondidos === 0 ? 'Responda pelo menos uma conversa antes' : undefined}
                onClick={() => void analisar()}
              >
                {ocupado === 'analise' ? <Loader2 className="girando" /> : <Sparkles />}
                {ocupado === 'analise' ? 'Analisando…' : 'Analisar'}
              </button>
              {casos.length > 0 && (
                <button
                  className="icone-btn perigo"
                  aria-label="Apagar treino"
                  title="Apagar treino"
                  onClick={() => setApagando(true)}
                >
                  <Trash2 />
                </button>
              )}
            </div>
          )}
          {dados.data && !dados.data.iaDisponivel && (
            <p className="erro-form">O treino precisa da IA ligada (IA_PROVEDOR=gemini no .env).</p>
          )}
        </header>

        <div className="lista-itens">
          {analises.length > 0 && (
            <div className="tr-grupo">
              <h2>Análises</h2>
              {analises.map((a) => (
                <NavLink key={a.id} to={`/treino/analise/${a.id}`} className="item tr-analise">
                  <FileText />
                  <span className="item-meio">
                    <span className="item-nome">Análise de {dataHora(a.criadoEm)}</span>
                    <span className="item-prev">{a.conversas} conversas analisadas</span>
                  </span>
                </NavLink>
              ))}
            </div>
          )}

          {casos.length === 0 ? (
            <p className="lista-vazia">
              {admin
                ? 'Nenhum cliente de treino ainda. Escolha quantos e clique em Nova rodada.'
                : 'Nenhum cliente de treino ainda. Peça pra um admin começar uma rodada.'}
            </p>
          ) : (
            <div className="tr-grupo">
              <h2>
                Clientes{' '}
                <small>
                  {respondidos} de {casos.length} respondidos
                </small>
              </h2>
              {casos.map((c) => (
                <NavLink
                  key={c.conversaId}
                  to={`/treino/${c.conversaId}`}
                  className={`item ${c.naoLidas > 0 && !c.encerrado ? 'nao-lida' : ''}`}
                >
                  <AvatarContato id={c.conversaId} nome={c.persona.nome} />
                  <span className="item-meio">
                    <span className="item-linha">
                      <span className="item-nome">
                        {c.persona.nome}
                        {c.persona.empresa && <small> · {c.persona.empresa}</small>}
                      </span>
                      {situacao(c)}
                    </span>
                    <span className="item-linha">
                      <span className="item-prev">{c.persona.material.replace(/-/g, ' ')}</span>
                      {c.naoLidas > 0 && !c.encerrado && (
                        <span className="badge">{c.naoLidas}</span>
                      )}
                    </span>
                  </span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      </section>

      {id ? (
        <Chat key={id} id={id} eu={eu} voltarPara="/treino" />
      ) : analise ? (
        <main className="tr-leitura">
          <header>
            <h1>Análise de {dataHora(analise.criadoEm)}</h1>
            <p>
              {analise.conversas} conversas analisadas. Revise e peça pro Claude aplicar o que
              estiver certo nas fichas.
            </p>
          </header>
          <Relatorio texto={analise.texto} />
        </main>
      ) : (
        <main className="chat-vazio">
          <Vazio icone={<GraduationCap className="grande" />} titulo="Treino da IA">
            Cada cliente aqui é simulado pela IA. Responda como você responderia no WhatsApp: as
            respostas viram conhecimento pra IA de atendimento.
            {admin && (
              <>
                <br />
                <CheckCircle2 className="tr-dica" /> No fim, clique em <b>Analisar</b>.
              </>
            )}
          </Vazio>
        </main>
      )}

      {apagando && (
        <Modal titulo="Apagar treino" aoFechar={() => setApagando(false)}>
          <p style={{ margin: 0 }}>
            Apagar os {casos.length} clientes de treino e as conversas? As análises continuam
            guardadas.
          </p>
          <div className="acoes">
            <button className="btn" onClick={() => setApagando(false)}>
              Cancelar
            </button>
            <button className="btn perigo-cheio" onClick={() => void apagar()}>
              Apagar
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
